import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { SERVICES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm text-navy-900 placeholder:text-slate-400 outline-none transition-colors focus:border-navy-800 focus:ring-2 focus:ring-navy-100";

const SLOTS = [
  "Morning (10 AM – 12 PM)",
  "Midday (12 – 3 PM)",
  "Afternoon (3 – 6 PM)",
  "Evening (6 – 8 PM)",
];

export default function LeadForm({ defaultService = "General enquiry", id = "lead" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: defaultService,
    preferred_date: "",
    preferred_slot: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received — a CA will call you back shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-lg border border-line bg-mist p-8 text-center" data-testid={`${id}-success`}>
        <CheckCircle2 className="mx-auto h-9 w-9 text-navy-800" />
        <p className="mt-4 text-lg font-semibold text-navy-900">Request received.</p>
        <p className="mt-2 text-sm leading-relaxed text-ink">
          A chartered accountant will call you back shortly — usually within a few working
          hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3.5" data-testid={`${id}-form`}>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <input
          className={inputClass}
          placeholder="Your name *"
          value={form.name}
          onChange={set("name")}
          data-testid={`${id}-name-input`}
        />
        <input
          className={inputClass}
          placeholder="Phone *"
          type="tel"
          value={form.phone}
          onChange={set("phone")}
          data-testid={`${id}-phone-input`}
        />
      </div>
      <input
        className={inputClass}
        placeholder="Email"
        type="email"
        value={form.email}
        onChange={set("email")}
        data-testid={`${id}-email-input`}
      />
      <select className={inputClass} value={form.service} onChange={set("service")} data-testid={`${id}-service-select`}>
        <option value="General enquiry">General enquiry</option>
        {SERVICES.map((s) => (
          <option key={s.slug} value={s.title}>
            {s.title}
          </option>
        ))}
      </select>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-slate-600">
            Preferred date
          </label>
          <input
            className={inputClass}
            type="date"
            min={today}
            value={form.preferred_date}
            onChange={set("preferred_date")}
            data-testid={`${id}-date-input`}
          />
        </div>
        <div>
          <label className="mb-1.5 block font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-slate-600">
            Preferred time
          </label>
          <select className={inputClass} value={form.preferred_slot} onChange={set("preferred_slot")} data-testid={`${id}-slot-select`}>
            <option value="">Any time</option>
            {SLOTS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <textarea
        className={`${inputClass} min-h-[88px] resize-y`}
        placeholder="Tell us briefly what you need"
        value={form.message}
        onChange={set("message")}
        data-testid={`${id}-message-input`}
      />
      <button
        type="submit"
        disabled={loading}
        className="mt-1 inline-flex items-center justify-center rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-800 disabled:opacity-60"
        data-testid={`${id}-submit-button`}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get a free callback"}
      </button>
      <p className="text-center text-sm text-slate-600">
        By submitting you agree to our{" "}
        <a href="/privacy-policy" className="font-medium underline hover:text-navy-900">privacy policy</a>.
      </p>
    </form>
  );
}
